import { useEffect, useRef } from 'react';

import { notifications } from '@mantine/notifications';

import { useUpdateBillingMutation } from '@/store/slices/api/billingApiSlice';
import { useGetTaskQuery } from '@/store/slices/api/tasksApiSlice';
import {
  useGetTransmissionReportDetailsQuery,
  usePatchTransmissionReportMutation,
  useUpdateMultipleNotesMutation
} from '@/store/slices/api/transmissionReport/transmissionReportsApiSlice';
import { getDisplaySignatures } from '@/utils/reportSignatures';

import { ReportLocationEnum } from '@/types/alert';
import { ReportSignature, SignatureStatus } from '@/types/transmissionReport';

import { reportFieldsFromLinkedTask } from './taskReportSync';

function reportFieldsAreLocked(
  signatureStatus: SignatureStatus,
  signatures: ReportSignature[]
): boolean {
  return (
    signatureStatus === SignatureStatus.Signed ||
    signatureStatus === SignatureStatus.NonPractitionerSigned ||
    signatureStatus === SignatureStatus.SignatureNotRequired ||
    getDisplaySignatures(signatures).length > 0
  );
}

export function useSyncLinkedTaskOntoReport(
  reportId: number,
  setIsOperationInProgress?: (isInProgress: boolean) => void
) {
  const { data, refetch } = useGetTransmissionReportDetailsQuery(reportId, { skip: !reportId });
  const report = data?.transmissionReport;
  const taskId = report?.taskId ?? '';
  const locked = report
    ? reportFieldsAreLocked(report.signatureStatus, report.signatures ?? [])
    : true;
  const { data: task } = useGetTaskQuery(taskId, { skip: !taskId || locked });

  const [patchReport] = usePatchTransmissionReportMutation();
  const [updateBilling] = useUpdateBillingMutation();
  const [updateNotes] = useUpdateMultipleNotesMutation();

  const syncedReportId = useRef<number | null>(null);

  useEffect(() => {
    if (!report || !task || locked || report.id !== reportId) return;
    if (syncedReportId.current === report.id) return;

    const changes = reportFieldsFromLinkedTask(task, {
      billingDate: report.billing?.billingDate,
      color: report.color,
      reportType: report.reportType,
      notes: report.notes
    });
    syncedReportId.current = report.id;
    if (!changes) return;

    setIsOperationInProgress?.(true);

    const apply = async () => {
      const reportPatch: { color?: typeof changes.color; reportType?: typeof changes.reportType } =
        {};
      if (changes.color) reportPatch.color = changes.color;
      if (changes.reportType) reportPatch.reportType = changes.reportType;
      if (reportPatch.color || reportPatch.reportType) {
        await patchReport({ id: report.id, ...reportPatch }).unwrap();
      }

      if (changes.billingDate !== undefined) {
        const billingId = report.billing?.id;
        if (!billingId) throw new Error('Report has no billing record');
        await updateBilling({
          billingId,
          data: {
            transmissionReportId: report.id,
            billingDate: changes.billingDate
          }
        }).unwrap();
      }

      if (changes.transmissionNote) {
        await updateNotes({
          transmissionReportId: report.id,
          notes: [
            {
              note: changes.transmissionNote,
              location: ReportLocationEnum.Transmission
            }
          ]
        }).unwrap();
      }

      await refetch();
    };

    void apply()
      .catch((error) => {
        console.error('Failed to copy linked task fields onto the report', error);
        notifications.show({
          title: 'Error',
          message: 'Could not copy the linked task onto this report',
          color: 'red'
        });
      })
      .finally(() => {
        setIsOperationInProgress?.(false);
      });
  }, [
    locked,
    patchReport,
    refetch,
    report,
    reportId,
    setIsOperationInProgress,
    task,
    updateBilling,
    updateNotes
  ]);
}
